import React, { useState } from "react";
import { Calculator, FileText, Settings, BarChart3, LogOut, Users, UserPlus, UserCheck, CreditCard, Building2, Clock } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "../../utils/cn";
import { useAuth } from '../../hooks/useAuth';

type SideNavProps = {
    sidebarNavIncludeIds?: string[];
    sidebarNavRemoveIds?: string[];
};

interface NavItem {
    id: string;
    title: string;
    href?: string;                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
    icon: React.ComponentType<{ className?: string }>;
    disabled?: boolean;
    external?: boolean;
    authorizeOnly?: "admin" | "user";
    subItems?: NavSubItem[];
}

interface NavSubItem {
    id: string;
    title: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    disabled?: boolean;
    external?: boolean;
}

const navigationItems: NavItem[] = [
    {
        id: "calculator",
        title: "Quote (Classic)",
        href: "/calculator",
        icon: Calculator,
    },
    {
        id: "new-quote",
        title: "Quote (New)",
        href: "/new-quote",
        icon: Calculator,
    },
    {
        id: "quotes",
        title: "Quotes",
        href: "/quotes?tab=recent",
        icon: FileText,
    },
    {
        id: "dashboard",
        title: "Dashboard",
        href: "/dashboard",
        icon: BarChart3,
        authorizeOnly: "admin",
    },
    {
        id: "company-dashboard",
        title: "Company Dashboard",
        href: "/company-dashboard",
        icon: BarChart3,
        authorizeOnly: "admin",
    },
    {
        id: "organization",
        title: "Organization",
        icon: Building2,
        authorizeOnly: "admin",
        subItems: [
            {
                id: "org-members",
                title: "Org Members",
                href: "/org/members",
                icon: UserCheck,
            },
            {
                id: "invite-members",
                title: "Invite Members",
                href: "/org/invite",
                icon: UserPlus,
            },
            {
                id: "plans-billing",
                title: "Plans & Billing",
                href: "/org/billing",
                icon: CreditCard,
            },
            {
                id: "org-settings",
                title: "Settings",
                href: "/org/settings",
                icon: Settings,
            },
        ],
    },
    {
        id: "settings",
        title: "Admin Settings",
        href: "/admin",
        icon: Settings,
        authorizeOnly: "admin",
    },
];

export function SidebarNav({
    sidebarNavIncludeIds,
    sidebarNavRemoveIds,
}: SideNavProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    // Filter navigation items based on include/remove IDs
    let filteredItems = navigationItems;

    if (sidebarNavIncludeIds && sidebarNavIncludeIds.length > 0) {
        filteredItems = filteredItems.filter(item => 
            sidebarNavIncludeIds.includes(item.id)
        );
    }

    if (sidebarNavRemoveIds && sidebarNavRemoveIds.length > 0) {
        filteredItems = filteredItems.filter(item => 
            !sidebarNavRemoveIds.includes(item.id)
        );
    }

    // Filter by authorization
    filteredItems = filteredItems.filter(item => {
        if (!item.authorizeOnly) return true;
        if (item.authorizeOnly === "admin") {
            return user?.role === "admin" || user?.role === "superadmin";
        }
        return true;
    });

    const toggleExpanded = (itemId: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const isActiveHref = (href?: string) => {
        if (!href) return false;
        const link = new URL(href, 'http://placeholder');
        if (location.pathname !== link.pathname) return false;
        if (!link.search) {
            return !location.search;
        }
        const linkParams = link.searchParams;
        const currentParams = new URLSearchParams(location.search);
        for (const [key, value] of linkParams.entries()) {
            if (currentParams.get(key) !== value) {
                return false;
            }
        }
        return true;
    };

    const renderNavItem = (item: NavItem) => {
        const Icon = item.icon;
        const isActive = isActiveHref(item.href);
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedItems.has(item.id);

        if (hasSubItems) {
            return (
                <div key={item.id} className="space-y-1">
                    <button
                        onClick={() => toggleExpanded(item.id)}
                        className={cn(
                            "flex items-center justify-between w-full rounded-md px-3 py-2 text-sm font-medium transition-colors",
                            "hover:bg-accent hover:text-accent-foreground",
                            item.disabled && "pointer-events-none opacity-50"
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4" />
                            <span>{item.title}</span>
                        </div>
                        <svg
                            className={cn(
                                "h-4 w-4 transition-transform",
                                isExpanded && "rotate-180"
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isExpanded && (
                        <div className="ml-4 space-y-1 border-l border-border pl-4">
                            {item.subItems!.map((subItem) => {
                                const SubIcon = subItem.icon;
                                const isSubActive = location.pathname === subItem.href;
                                
                                return (
                                    <button
                                        key={subItem.id}
                                        onClick={() => navigate(subItem.href)}
                                        className={cn(
                                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full",
                                            "hover:bg-accent hover:text-accent-foreground",
                                            isSubActive && "bg-accent text-accent-foreground",
                                            subItem.disabled && "pointer-events-none opacity-50"
                                        )}
                                    >
                                        <SubIcon className="h-4 w-4" />
                                        <span>{subItem.title}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Regular navigation item
        return (
            <button
                key={item.id}
                onClick={() => item.href && navigate(item.href)}
                disabled={!item.href}
                className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full",
                    "hover:bg-accent hover:text-accent-foreground",
                    isActive && "bg-accent text-accent-foreground",
                    item.disabled && "pointer-events-none opacity-50",
                    !item.href && "cursor-default"
                )}
            >
                <Icon className="h-4 w-4" />
                <span>{item.title}</span>
            </button>
        );
    };

    return (
        <nav className="flex flex-col space-y-1">
            {filteredItems.map(renderNavItem)}
            
            {/* Logout option */}
            <div className="pt-4 mt-4 border-t border-border">
                <button
                    onClick={async () => {
                        await logout('https://estimation.pk');
                    }}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full hover:bg-accent hover:text-accent-foreground"
                >
                    <LogOut className="h-4 w-4" />
                    <span>Log out</span>
                </button>
            </div>
        </nav>
    );
}
